/**
 * AdminApprovals — Unified approvals command center (iter100.228).
 *
 * Consolidates three previously-separate sidebar entries:
 *   • Content Review  → doctor-authored testimonials & case studies (microsite)
 *   • Course Reviews  → certification-author course submissions (Academy)
 *   • Case Validations → doctor case proof for course certification
 *
 * Each existing page component is imported as-is and rendered under its own
 * tab. Deep-links via `?tab=<slug>`. Pending counts are pinged on mount and
 * surfaced as badges on the tab buttons so an admin can see everything they
 * need to review at a glance.
 *
 * Legacy routes (/admin/content-review, /admin/course-reviews,
 * /admin/case-validations) still work — they redirect here with the right
 * `?tab=` slug (see App.js).
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Storefront, GraduationCap, ClipboardText, CheckSquareOffset } from "@phosphor-icons/react";
import api from "@/lib/api";

import AdminContentReview from "@/pages/admin/AdminContentReview";
import CourseReviewInbox from "@/pages/admin/CourseReviewInbox";
import CaseValidationInbox from "@/pages/admin/CaseValidationInbox";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";


const TABS = [
    { id: "content",     label: "Microsite Content",  Icon: Storefront,     hint: "Testimonials + case studies awaiting editorial approval" },
    { id: "courses",     label: "Course Submissions", Icon: GraduationCap,  hint: "Certification-author courses awaiting publish approval" },
    { id: "case-proofs", label: "Case Validations",   Icon: ClipboardText,  hint: "Doctor case submissions for course certification" },
];


export default function AdminApprovals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = TABS.some((t) => t.id === searchParams.get("tab"))
        ? searchParams.get("tab")
        : "content";
    const [tab, setTab] = useState(initialTab);
    const [counts, setCounts] = useState({ content: 0, courses: 0, "case-proofs": 0 });

    /* Keep URL param in sync (mirrors CoeTeam pattern). */
    useEffect(() => {
        if (tab === searchParams.get("tab")) return;
        if (tab === "content") {
            searchParams.delete("tab");
            setSearchParams(searchParams, { replace: true });
        } else {
            searchParams.set("tab", tab);
            setSearchParams(searchParams, { replace: true });
        }
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Pending-count badges. Each endpoint returns something different so we
       normalize into a plain integer per tab and swallow errors quietly (a
       stale badge is better than crashing the whole page). */
    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([
            api.get("/admin/content-review/stats"),
            api.get("/admin/course-review/inbox"),
            api.get("/admin/case-validations?status=pending"),
        ]).then((results) => {
            if (cancelled) return;
            const next = { content: 0, courses: 0, "case-proofs": 0 };
            if (results[0].status === "fulfilled") {
                /* content-review/stats key is `pending_review` (not `pending`). */
                next.content = Number(results[0].value.data?.pending_review || 0);
            }
            if (results[1].status === "fulfilled") {
                next.courses = Array.isArray(results[1].value.data) ? results[1].value.data.length : 0;
            }
            if (results[2].status === "fulfilled") {
                const d = results[2].value.data;
                next["case-proofs"] = Number(d?.counts?.pending || 0);
            }
            setCounts(next);
        });
        return () => { cancelled = true; };
    }, [tab]); // re-poll on tab change so counts refresh after acting

    const totalPending = counts.content + counts.courses + counts["case-proofs"];

    return (
        <div className="space-y-6" data-testid="admin-approvals-page">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Content · Approvals</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Approvals</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        One inbox for microsite content, courses, and case validations awaiting review.
                        {" "}Pending:{" "}
                        <span className="font-semibold text-foreground tabular-nums" data-testid="approvals-total-pending">
                            {totalPending}
                        </span>.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                        <CheckSquareOffset size={14} weight="duotone" />
                        {totalPending === 0 ? "All caught up" : `${totalPending} pending`}
                    </div>
                </div>
            </div>

            <AdminSegmentedTabs
                tabs={TABS.map((t) => ({
                    id: t.id,
                    label: t.label,
                    Icon: t.Icon,
                    hint: t.hint,
                    count: counts[t.id] || 0,
                    tone: "warning",
                }))}
                active={tab}
                onChange={setTab}
                testid="approvals-tabs"
                tabTestidBase="approvals-tab"
                showZero
            />

            <div>
                {tab === "content"     && <AdminContentReview />}
                {tab === "courses"     && <CourseReviewInbox />}
                {tab === "case-proofs" && <CaseValidationInbox />}
            </div>
        </div>
    );
}
